import { useState } from 'react';
import { X, Search, Plus, Check, Loader2, Package } from 'lucide-react';
import { EmptyState, ErrorState } from '@mustauto/ui/web';
import { useAuth } from '@/hooks/useAuth';
import {
  useCatalogList,
  useCatalogSuggestions,
  useLinkProducts,
  type CatalogProduct,
} from '@/hooks/useCatalog';
import { Badge } from './ui/badge';
import { formatReais } from '@/lib/utils';

interface Props {
  campaignId: string;
  linkedProductIds: Set<string>;
  onClose: () => void;
  onCreateNew: () => void;
}

export function CatalogLinkModal({ campaignId, linkedProductIds, onClose, onCreateNew }: Props) {
  const { user } = useAuth();
  const pid = user?.partnerId ?? '';

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: suggestions, isLoading: suggestionsLoading } = useCatalogSuggestions(pid, campaignId);
  const { data: catalogData, isLoading: catalogLoading, isError: catalogError, error: catalogErrorObj, refetch: refetchCatalog } = useCatalogList(pid, {
    search: search || undefined,
    category: category || undefined,
    status: 'active',
    limit: 50,
  });

  const linkMutation = useLinkProducts(pid, campaignId);

  function toggleSelect(id: string) {
    if (linkedProductIds.has(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleLink() {
    if (selected.size === 0) return;
    linkMutation.mutate([...selected], { onSuccess: () => onClose() });
  }

  function renderProductRow(product: CatalogProduct) {
    const isLinked = linkedProductIds.has(product.id);
    const isSelected = selected.has(product.id);

    return (
      <div
        key={product.id}
        onClick={() => toggleSelect(product.id)}
        className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
          isLinked ? 'border-border bg-muted/50 opacity-60 cursor-default' :
          isSelected ? 'border-primary bg-accent' :
          'border-border hover:bg-muted'
        }`}
      >
        <div className="flex h-5 w-5 items-center justify-center">
          {isLinked ? (
            <Check size={16} className="text-muted-foreground" />
          ) : (
            <input
              type="checkbox"
              checked={isSelected}
              readOnly
              className="h-4 w-4 rounded border-border accent-primary"
            />
          )}
        </div>

        {product.photoUrl ? (
          <img src={product.photoUrl} alt={product.name} className="h-10 w-10 rounded object-cover" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded bg-muted">
            <Package size={16} className="text-muted-foreground" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{product.name}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {product.category && <span>{product.category}</span>}
            {product.price && <span>{formatReais(Number(product.price))}</span>}
          </div>
        </div>

        {isLinked && (
          <Badge variant="secondary" className="shrink-0">Já nesta campanha</Badge>
        )}
      </div>
    );
  }

  // Build suggestion groups
  const suggestedProducts = suggestions?.flatMap((s) => s.products) ?? [];
  const suggestedIds = new Set(suggestedProducts.map((p) => p.id));
  const otherProducts = (catalogData?.products ?? []).filter((p) => !suggestedIds.has(p.id));

  const isLoading = suggestionsLoading || catalogLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-full max-w-[600px] rounded-xl bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-bold">Vincular produtos do catálogo</h2>
          <button aria-label="Fechar" onClick={onClose} className="rounded p-1 hover:bg-muted"><X size={18} /></button>
        </div>

        {/* Search + Filter */}
        <div className="border-b p-4 space-y-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar no catálogo..."
              className="w-full rounded-lg border border-border bg-muted pl-9 pr-4 py-2 text-sm outline-none focus:border-primary"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCreateNew}
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-primary px-3 py-1.5 text-xs font-medium text-primary hover:bg-accent"
            >
              <Plus size={12} /> Criar novo produto
            </button>
          </div>
        </div>

        {/* Product list */}
        <div className="max-h-[50vh] overflow-y-auto p-4 space-y-4">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {!isLoading && suggestedProducts.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Sugeridos para esta campanha
              </div>
              <div className="space-y-2">
                {suggestedProducts.map(renderProductRow)}
              </div>
            </div>
          )}

          {!isLoading && otherProducts.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {suggestedProducts.length > 0 ? 'Outros produtos' : 'Catálogo'}
              </div>
              {suggestedProducts.length > 0 && otherProducts.length > 0 && (
                <div className="mb-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                  Estes produtos são de categorias diferentes da campanha
                </div>
              )}
              <div className="space-y-2">
                {otherProducts.map(renderProductRow)}
              </div>
            </div>
          )}

          {/* "Catálogo vazio · Crie produtos antes de vincular" para quem tem
              catálogo cheio manda o parceiro cadastrar tudo de novo. */}
          {!isLoading && catalogError && suggestedProducts.length === 0 && otherProducts.length === 0 && (
            <ErrorState
              title="Não foi possível carregar seu catálogo"
              error={catalogErrorObj}
              onRetry={() => void refetchCatalog()}
            />
          )}

          {/* Busca sem resultado nao e catalogo vazio: a filtragem e do
              servidor, entao um termo que nao casa chegava aqui como lista
              vazia e mandava o parceiro cadastrar o que ele ja tem. */}
          {!isLoading && !catalogError && (search || category) && suggestedProducts.length === 0 && otherProducts.length === 0 && (
            <EmptyState
              compact
              icon={<Search size={26} />}
              title="Nenhum produto com esses filtros"
              description="Ajuste a busca ou a categoria para encontrar o que já está no seu catálogo."
              action={{ label: 'Limpar filtros', onClick: () => { setSearch(''); setCategory(''); } }}
            />
          )}

          {!isLoading && !catalogError && !search && !category && suggestedProducts.length === 0 && otherProducts.length === 0 && (
            <EmptyState
              compact
              icon={<Package size={26} />}
              title="Catálogo vazio"
              description="Crie produtos no catálogo mestre antes de vinculá-los a uma campanha."
              action={{ label: 'Criar produto', onClick: onCreateNew }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            {selected.size > 0 ? `${selected.size} selecionado(s)` : 'Selecione produtos'}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted">
              Cancelar
            </button>
            <button onClick={handleLink} disabled={selected.size === 0 || linkMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
              {linkMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
              Vincular {selected.size > 0 ? `${selected.size} produto(s)` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
